// apps/web/src/api/russia-requests/chat.ts
//
// Typed wrappers around the RF chat endpoints introduced in #1066.
// Mirrors the China `apps/web/src/api/requests/chat.ts` shape so the
// drawer pattern stays identical, but every call routes through the
// RF-scoped controller (`/russia-requests/*`) so wire layer remains
// isolated per the schema-isolation invariant.
//
// @aiSecurity backend re-applies the RF chat-party gate
//   (admin · authoring manager · acting logist) on every endpoint; the
//   client cannot bypass it by recombining ids.

import { z, type ZodType } from 'zod';
import {
  RussiaRequestChatMessageSchema,
  RussiaRequestChatListResponseSchema,
  RussiaRequestChatReadBodySchema,
  RussiaRequestChatUnreadSummaryResponseSchema,
  type RussiaRequestChatMessage,
  type RussiaRequestChatListResponse,
  type RussiaRequestChatReadBody,
  type RussiaRequestChatUnreadSummaryResponse,
} from '@magimp/shared';
import { request } from '../client.js';
import { withQuery } from './_query.js';

export type { RussiaRequestChatListResponse };

export function listRussiaRequestChat(
  id: string,
  query: { cursor?: string; limit?: number } = {},
): Promise<RussiaRequestChatListResponse> {
  const params = new URLSearchParams();
  if (query.cursor) params.set('cursor', query.cursor);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  const qs = params.toString();
  return request<RussiaRequestChatListResponse>(
    withQuery(`/russia-requests/${encodeURIComponent(id)}/chat`, qs),
    { method: 'GET' },
    {},
    RussiaRequestChatListResponseSchema as unknown as ZodType<RussiaRequestChatListResponse>,
  );
}

export function postRussiaRequestChat(id: string, body: string): Promise<RussiaRequestChatMessage> {
  return request<RussiaRequestChatMessage>(
    `/russia-requests/${encodeURIComponent(id)}/chat`,
    {
      method: 'POST',
      body: JSON.stringify({ body }),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RussiaRequestChatMessageSchema as unknown as ZodType<RussiaRequestChatMessage>,
  );
}

// WHY (#1066): the unread-summary endpoint accepts up to 200 `russiaRequestId`
// query params (repeated key shape `?russiaRequestId=RU-00001&russiaRequestId=RU-00002...`).
// Beyond the cap the server returns VALIDATION_ERROR; consumers must chunk.
export function fetchRussiaRequestChatUnreadSummary(
  russiaRequestIds: readonly string[],
  opts: { signal?: AbortSignal } = {},
): Promise<RussiaRequestChatUnreadSummaryResponse> {
  const params = new URLSearchParams();
  for (const id of russiaRequestIds) params.append('russiaRequestId', id);
  return request<RussiaRequestChatUnreadSummaryResponse>(
    withQuery('/russia-requests/chat-unread-summary', params.toString()),
    { method: 'GET' },
    opts.signal ? { signal: opts.signal } : {},
    RussiaRequestChatUnreadSummaryResponseSchema as unknown as ZodType<RussiaRequestChatUnreadSummaryResponse>,
  );
}

export function markRussiaRequestChatRead(
  id: string,
  body: RussiaRequestChatReadBody,
): Promise<void> {
  return request<void>(
    `/russia-requests/${encodeURIComponent(id)}/chat/read`,
    {
      method: 'POST',
      body: JSON.stringify(RussiaRequestChatReadBodySchema.parse(body)),
      headers: { 'content-type': 'application/json' },
    },
    {},
    z.void(),
  );
}

export function markRussiaRequestChatUnread(id: string): Promise<void> {
  return request<void>(
    `/russia-requests/${encodeURIComponent(id)}/chat/mark-unread`,
    { method: 'POST' },
    {},
    z.void(),
  );
}

export function unmarkRussiaRequestChatUnread(id: string): Promise<void> {
  return request<void>(
    `/russia-requests/${encodeURIComponent(id)}/chat/unmark-unread`,
    { method: 'POST' },
    {},
    z.void(),
  );
}
